import { Component, OnInit, Output, EventEmitter, OnDestroy, ChangeDetectorRef, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { Product } from './product-select.model';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FormGroup, FormControl, ValidationErrors, AbstractControl } from '@angular/forms';
import { Dropdown } from 'primeng/dropdown';

/**
 * @description Component for selecting a product from a dropdown list.
 * Handles product display based on language, validation, mobile view, and adding new products.
 */
@Component({
	selector: 'app-product-select',
	templateUrl: './product-select.component.html',
	styleUrls: ['./product-select.component.scss'],
	providers: [MessageService]
})
export class ProductSelectComponent implements OnInit, OnDestroy, OnChanges {
	/**
	 * @description The parent FormGroup instance.
	 */
	@Input() parentFormGroup: FormGroup;
	/**
	 * @description The specific FormControl instance within the parent FormGroup to bind to.
	 */
	@Input() formControlInstance: FormControl | AbstractControl | null = null;
	/**
	 * @description The label text to display for the dropdown.
	 * @default 'Product'
	 */
	@Input() label: string = 'Product';
	/**
	 * @description Whether the product selection is required.
	 * @default false
	 */
	@Input() required: boolean = false;
	/**
	 * @description Flag indicating if the parent form has been submitted, used for validation display.
	 * @default false
	 */
	@Input() submitted: boolean = false;
	/**
	 * @description Enables debug logging (currently unused).
	 * @default false
	 */
	@Input() debug: boolean = false;
	/**
	 * @description The list of available products to display in the dropdown.
	 * @default []
	 */
	@Input() productsList: Product[] = [];
	/** Company on the current document. Prefills and locks the add-product dialog. */
	@Input() companyId: number | null = null;
	/**
	 * @description Event emitter that fires when a product is selected or deselected.
	 * Emits the selected Product object or null.
	 */
	@Output() productChange = new EventEmitter<Product | null>();

	/**
	 * @description The current application language code ('en', 'ar', 'he').
	 */
	curentLang: string = localStorage.getItem('curentLang') || 'en';
	/**
	 * @description Flag indicating if the component has products and is ready for interaction.
	 */
	public isReady: boolean = false;
	/**
	 * @description Internal reference to the bound FormControl.
	 */
	public control: FormControl | null = null;
	/**
	 * @internal
	 * @description Subscription to the parent form control's value changes.
	 */
	private parentValueSub: Subscription | null = null;
	/**
	 * @internal
	 * @description Subject used to signal component destruction for unsubscribing observables.
	 */
	private destroy$ = new Subject<void>();

	/**
	 * @description Reference to the PrimeNG Dropdown component instance in the template.
	 */
	@ViewChild('dropdownRef') dropdown: Dropdown | undefined;

	/**
	 * @description State flag for the mobile-specific dropdown modal.
	 */
	isMobileDropdownOpen = false;
	/**
	 * @description Search term entered by the user in the mobile dropdown filter.
	 */
	searchTerm: string = '';

	/**
	 * @description State flag for the 'Add Product' modal dialog.
	 */
	showAddProductModal: boolean = false;

	private maximizedOnce: boolean = false;

	/**
	 * @description Injects required services: MessageService, TranslateService, ChangeDetectorRef.
	 * @param messageService Service for displaying messages (e.g., toasts).
	 * @param translate Service for handling internationalization (i18n).
	 * @param cdr Service for manually triggering change detection.
	 */
	constructor(
		private messageService: MessageService,
		private translate: TranslateService,
		private cdr: ChangeDetectorRef
	) { }

	/**
	 * @description Angular lifecycle hook called after every check of the component's view.
	 * Used here to maximize the dialog automatically on small screens when it first opens.
	 */
	ngAfterViewChecked(): void {
		if (
			this.isMobileDropdownOpen &&
			window.innerWidth < 768 &&
			!this.maximizedOnce
		) {
			this.clickMaximizeButton();
			this.maximizedOnce = true;
		}
	}

	/**
	 * @protected
	 * @description Programmatically clicks the maximize button of the PrimeNG dialog.
	 * Used for responsive behavior on small screens.
	 */
	protected clickMaximizeButton() {
		// Note: This relies on PrimeNG's v13 internal CSS classes and might break with updates.
		const dialogEl = document.querySelector('.p-dialog') as HTMLElement;
		if (dialogEl) {
			const maximizeBtn = dialogEl.querySelector('.p-dialog-header-maximize') as HTMLElement;
			if (maximizeBtn) {
				maximizeBtn.click();
			}
		}
	}

	/**
	 * @description Angular lifecycle hook called on component initialization.
	 * Sets up the internal form control reference and checks initial readiness based on productsList.
	 */
	ngOnInit(): void {
		this.setupControlFromInstance();
		this.checkIfReady(this.productsList);
	}

	/**
	 * @description Angular lifecycle hook called when any data-bound input property changes.
	 * Handles updates to formControlInstance, productsList, and submitted status.
	 * @param changes Object containing the changed properties.
	 */
	ngOnChanges(changes: SimpleChanges): void {
		if (changes['formControlInstance']) {
			this.setupControlFromInstance();
		}

		if (changes['productsList']) {
			const newList = changes['productsList'].currentValue as Product[];
			this.checkIfReady(newList);
			this.triggerChangeDetection();
		}

		if (changes['submitted']) {
			this.submitted = changes['submitted'].currentValue;
			this.cdr.markForCheck();
		}
	}

	/**
	 * @description Angular lifecycle hook called just before the component is destroyed.
	 * Completes the destroy$ subject to clean up subscriptions.
	 */
	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		if (this.parentValueSub) {
			this.parentValueSub.unsubscribe();
		}
	}

	/**
	 * @internal
	 * @description Sets the internal `control` reference based on the `formControlInstance` input.
	 * Subscribes to value changes if the instance is valid and sets the initial disabled state.
	 */
	private setupControlFromInstance(): void {
		if (this.formControlInstance instanceof FormControl) {
			if (this.control !== this.formControlInstance) {
				this.control = this.formControlInstance;
				this.subscribeToValueChanges();

				if (!this.productsList || this.productsList.length === 0) {
					this.control.disable({ emitEvent: false });
				} else {
					this.control.enable({ emitEvent: false });
				}
			}
		} else {
			this.control = null;
			if (this.parentValueSub) {
				this.parentValueSub.unsubscribe();
				this.parentValueSub = null;
			}
		}
		this.cdr.markForCheck();
	}

	/**
	 * @internal
	 * @description Subscribes to the `valueChanges` observable of the internal form control.
	 * Ensures the component's view updates when the control's value changes externally.
	 */
	private subscribeToValueChanges(): void {
		if (this.parentValueSub) {
			this.parentValueSub.unsubscribe();
			this.parentValueSub = null;
		}

		if (!this.control) {
			return;
		}

		this.parentValueSub = this.control.valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe(value => {
				this.cdr.markForCheck();
			});
	}

	/**
	 * @internal
	 * @description Updates the `isReady` flag based on the presence of products.
	 * Enables or disables the form control accordingly.
	 * Includes a workaround to ensure the dropdown displays the correct initial value when products load asynchronously.
	 * @param products The list of products to check.
	 */
	private checkIfReady(products: Product[] | null | undefined): void {
		const previouslyReady = this.isReady;
		this.isReady = !!products && products.length > 0;

		if (this.isReady && !previouslyReady) {
			this.control?.enable({ emitEvent: false });
			this.cdr.markForCheck();

			 setTimeout(() => {
				if (this.control && this.control.value !== null && this.control.value !== undefined) {
					const currentValue = this.control.value;
					const valueExists = this.productsList?.some(p => p.product_id === currentValue);

					if (valueExists) {
						this.control.setValue(null, { emitEvent: false });
						this.control.setValue(currentValue, { emitEvent: false });
						this.cdr.markForCheck();
					}
				}
			}, 100);

		} else if (!this.isReady && previouslyReady) {
			this.control?.disable({ emitEvent: false });
			this.cdr.markForCheck();
		}
	}

	/**
	 * @description Public method to allow parent components to manually trigger change detection for this component.
	 */
	public triggerChangeDetection(): void {
		this.cdr.markForCheck();
	}

	/**
	 * @description Handles the (onChange) event from the PrimeNG dropdown component.
	 * Extracts the selected product ID and finds the corresponding Product object.
	 * @param event The event object emitted by the p-dropdown. Contains the selected value (product_id).
	 */
	onDropdownChange(event: any): void {
		const selectedProductId = event.value;
		const selectedProduct = this.productsList.find(p => p.product_id === selectedProductId) || null;
		this.onProductSelectionChange(selectedProduct);
	}

	/**
	 * @description Processes the product selection change.
	 * Updates the bound form control's value (if different), emits the `productChange` event,
	 * and marks the control as interacted (touched and dirty).
	 * @param selectedProduct The selected Product object or null if deselected.
	 */
	onProductSelectionChange(selectedProduct: Product | null): void {
		const selectedId = selectedProduct ? selectedProduct.product_id : null;

		if (this.control && this.control.value !== selectedId) {
			this.control.setValue(selectedId, { emitEvent: false });
		}

		this.productChange.emit(selectedProduct);
		this.markAsInteracted();
	}

	/**
	 * @description Determines the property name ('product_name_en', 'product_name_ar', 'product_name_he')
	 * to use for displaying the product name based on the current language.
	 * @returns The property name string.
	 */
	getProductNameProperty(): string {
		switch (this.curentLang) {
			case 'ar': return 'product_name_ar';
			case 'he': return 'product_name_he';
			default: return 'product_name_en';
		}
	}

	/**
	 * @description Retrieves the product name in the appropriate language for display, with fallbacks.
	 * @param item The Product object.
	 * @returns The localized product name string, or an empty string if not found.
	 */
	getProductName(item: Product | null): string {
		if (!item) return '';
		switch (this.curentLang) {
			case 'ar': return item.product_name_ar || item.product_name_he || item.product_name_en || '';
			case 'he': return item.product_name_he || item.product_name_en || '';
			default: return item.product_name_en || item.product_name_he || '';
		}
	}

	/**
	 * @description Marks the internal form control as dirty and touched to trigger validation messages.
	 */
	markAsInteracted(): void {
		if (this.control) {
			this.control.markAsDirty();
			this.control.markAsTouched();
			this.cdr.markForCheck();
		}
	}

	/**
	 * @description Checks if the control should be displayed as invalid (invalid state and either touched or submitted).
	 * @returns True if the control is invalid and should show validation errors, false otherwise.
	 */
	isInvalid(): boolean {
		if (!this.control) {
			return false;
		}
		return this.control.invalid && (this.control.touched || this.submitted);
	}

	/**
	 * @description Gets the first validation error message translated for the current language.
	 * @returns The translated error message string, or an empty string if the control is valid or has no errors.
	 */
	getErrorMessage(): string {
		if (!this.control || !this.isInvalid() || !this.control.errors) {
			return '';
		}
		const errors: ValidationErrors = this.control.errors;
		const fieldName = this.label || 'Product';

		if (errors['required']) {
			return this.translate.instant('global.Validation.Required', { field: fieldName });
		}

		const firstErrorKey = Object.keys(errors)[0];
		return this.translate.instant('global.Validation.Invalid', { field: fieldName, error: firstErrorKey });
	}

	/**
	 * @description Gets the list of products filtered by the `searchTerm` entered in the mobile view.
	 * Filtering is case-insensitive and based on the localized product name.
	 * @returns The filtered array of Product objects.
	 */
	get filteredProducts(): Product[] {
		const term = this.searchTerm.trim().toLowerCase();
		if (!term) return this.productsList;
		return this.productsList.filter(p =>
			(this.getProductName(p) || '').toLowerCase().includes(term)
		);
	}

	/**
	 * @description Opens the modal used for product selection on mobile devices.
	 */
	openMobileDropdown(): void {
		this.isMobileDropdownOpen = true;
	}

	/**
	 * @description Closes the modal used for product selection on mobile devices.
	 */
	closeMobileDropdown(): void {
		this.isMobileDropdownOpen = false;
	}

	/**
	 * @description Checks if the current device is likely a mobile device based on screen width or user agent.
	 * @returns True if the device is considered mobile, false otherwise.
	 */
	isMobile(): boolean {
		return window.innerWidth <= 600 || /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(navigator.userAgent);
	}

	/**
	 * @description Gets the localized name of the currently selected product.
	 * Used for display purposes, e.g., in the mobile trigger button.
	 * @returns The localized name of the selected product, or an empty string if none is selected.
	 */
	getSelectedProductName(): string {
		const selected = this.productsList.find(p => p.product_id === this.control?.value);
		return selected ? this.getProductName(selected) : '';
	}

	/**
	 * @description Selects a product (typically called from the mobile selection list).
	 * Updates the form control, emits the change, and closes the mobile dropdown.
	 * @param product The Product object to select.
	 */
	selectProduct(product: Product): void {
		this.onProductSelectionChange(product);
		this.closeMobileDropdown();
	}

	/**
	 * @description Opens the modal dialog for adding a new product.
	 */
	openAddProductModal(): void {
		this.showAddProductModal = true;
	}

	/**
	 * @description Handles the save event from the 'Add Product' modal.
	 * Adds the new product to the list, closes the modal, selects the new product,
	 * and triggers change detection.
	 * @param newProduct The newly created Product object.
	 */
	handleProductModalSave(newProduct: Product): void {
		if (newProduct && newProduct.product_id) {
			this.productsList = [...this.productsList, newProduct];
			this.showAddProductModal = false;
			setTimeout(() => {
				this.control?.setValue(newProduct.product_id);
				this.productChange.emit(newProduct);
				this.cdr.detectChanges();
			});
		} else {
			this.showAddProductModal = false;
		}
	}

	/**
	 * @description Handles the close event from the 'Add Product' modal.
	 */
	handleProductModalClose(): void {
		this.showAddProductModal = false;
	}

	/**
	 * @description Retrieves the product description in the appropriate language for display, with fallbacks.
	 * @param item The Product object.
	 * @returns The localized product description string, or an empty string if not found.
	 */
	getProductDescription(item: Product | null): string {
		if (!item) return '';
		switch (this.curentLang) {
			case 'ar': return item.des_ar || item.des_he || item.des_en || '';
			case 'he': return item.des_he || item.des_en || '';
			default: return item.des_en || item.des_he || '';
		}
	}
}